import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EscDepthCtx, useEscLayer } from '../../utils/escLayer';
import { field } from './dialog';

// A searchable account picker: type a code or a name, pick from what is left, code and name shown on every row
// (Sales › Products & accounts, owner 2026-11-02). It lists what it is given and decides nothing about which
// accounts may be offered: that is the caller's (and the server's) to say.

export type ComboAccount = { code: string; name_th: string; name_en?: string };
export const accountLabel = (a: ComboAccount): string => `${a.code} · ${a.name_th}${a.name_en ? ` · ${a.name_en}` : ''}`;

// Esc while the list is open closes the list, not the window behind it: one layer deeper on the one Esc stack, for as
// long as this is mounted (utils/escLayer.ts).
function ListEsc({ onEsc }: { onEsc: () => void }) {
  const depth = useContext(EscDepthCtx) + 1;
  useEscLayer(onEsc, depth);
  return null;
}

export function AccountCombo({ accounts, value, onChange, placeholder, emptyLabel, createLabel, onCreate, invalid, id }: {
  accounts: ComboAccount[];
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  emptyLabel: string;
  /** The row under the list that opens the caller's new-account window; absent for whoever may not add one. */
  createLabel?: string;
  onCreate?: () => void;
  invalid?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const current = accounts.find(a => a.code === value);
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return accounts;
    return accounts.filter(a => `${a.code} ${a.name_th} ${a.name_en || ''}`.toLowerCase().includes(term));
  }, [accounts, q]);
  // Opening starts on the account already chosen, so Enter keeps it; a search starts on its first answer.
  useEffect(() => { setHi(q.trim() ? 0 : Math.max(0, shown.findIndex(a => a.code === value))); }, [q, open]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The highlighted row stays in sight while the arrows walk a long chart.
  useEffect(() => { if (open) (list.current?.children[hi] as any)?.scrollIntoView?.({ block: 'nearest' }); }, [hi, open]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  // The label is left selected, so whatever comes next (a letter, Backspace, a paste) replaces it and starts a new search.
  const pick = (code: string) => { onChange(code); setOpen(false); setQ(''); setTimeout(() => input.current?.select(), 0); };
  const canCreate = !!(onCreate && createLabel);
  const onKey = (e: React.KeyboardEvent) => {
    // The create row is the last stop of the arrows, so it can be reached without the mouse.
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setHi(h => Math.min(h + 1, shown.length - (canCreate ? 0 : 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (shown[hi]) pick(shown[hi].code);
      else if (canCreate && hi === shown.length) { setOpen(false); onCreate!(); }
    }
    else if (e.key === 'Tab') setOpen(false);
    // Typing after a pick starts a new search, not one that begins with the picked account's whole label.
    else if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { setQ(''); setOpen(true); }
  };
  const rowStyle = (on: boolean): React.CSSProperties => ({
    padding: '7px 10px', fontSize: 12.5, cursor: 'pointer', background: on ? 'var(--accent-bg)' : 'transparent', color: 'var(--text-primary)',
  });
  return (
    <div ref={box} style={{ position: 'relative' }}>
      {open && <ListEsc onEsc={() => setOpen(false)} />}
      <input ref={input} id={id} role="combobox" aria-expanded={open} autoComplete="off" placeholder={placeholder}
        style={{ ...field, ...(invalid ? { borderColor: '#B42318' } : {}) }}
        value={open ? q : (current ? accountLabel(current) : value)}
        onFocus={() => { setOpen(true); setQ(''); }}
        onClick={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey} />
      {open && (
        <div ref={list} role="listbox" style={{ position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, marginTop: 3, maxHeight: 260, overflowY: 'auto',
          background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg, 0 6px 18px rgba(0,0,0,.14))' }}>
          {shown.map((a, i) => (
            <div key={a.code} role="option" aria-selected={a.code === value} style={{ ...rowStyle(i === hi), ...(a.code === value ? { fontWeight: 700 } : {}) }}
              onMouseEnter={() => setHi(i)} onMouseDown={e => { e.preventDefault(); pick(a.code); }}>
              {accountLabel(a)}
            </div>
          ))}
          {!shown.length && <div style={{ ...rowStyle(false), cursor: 'default', color: 'var(--text-tertiary)' }}>{emptyLabel}</div>}
          {onCreate && createLabel && (
            <div role="button" data-combo-create style={{ ...rowStyle(hi === shown.length), fontWeight: 700, color: 'var(--accent-text)', borderTop: '1px solid var(--border)' }}
              onMouseEnter={() => setHi(shown.length)} onMouseDown={e => { e.preventDefault(); setOpen(false); onCreate(); }}>
              {createLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
